import {
  CustomVegaFormatTypes,
  DatetimeColumnDisplayFormat,
  NumberColumnDisplayFormat,
} from "@hex/common";
import { vega } from "vega-embed";

import {
  getDatetimeFormatter,
  getNumberFormatter,
} from "../../components/format/data/displayFormatFormatters";

export const customVegaInitialization = (vegaLib: typeof vega = vega): void => {
  if (
    vegaLib.expressionFunction(CustomVegaFormatTypes.NUMBER_FORMATTER) == null
  ) {
    vegaLib.expressionFunction(
      CustomVegaFormatTypes.NUMBER_FORMATTER,
      (val: number, params: unknown) => {
        if (!NumberColumnDisplayFormat.guard(params)) {
          return val;
        }

        const formatter = getNumberFormatter(params);
        return formatter?.({ value: val }) ?? val;
      },
    );
  }

  if (
    vegaLib.expressionFunction(CustomVegaFormatTypes.DATETIME_FORMATTER) == null
  ) {
    vegaLib.expressionFunction(
      CustomVegaFormatTypes.DATETIME_FORMATTER,
      (val: Date | number, params: unknown) => {
        if (!DatetimeColumnDisplayFormat.guard(params)) {
          return val;
        }

        const formatter = getDatetimeFormatter(params);
        // noticed during testing that we could sometimes get Date objects passed in
        // as a parameter, so account for that.
        const date = val instanceof Date ? val : new Date(val);
        // INFTY-1175: vegafusion returns datetimes without timezones (e.g. 2023-05-01T00:00:00).
        // When we're custom formatting our datetimes, we end up showing the wrong value because
        // locally (in SF, tz=PDT), vega evaluates 2023-05-01T00:00:00.000 as 2023-05-01T00:00:00.000-07:00.
        // For what it's worth, this is fine for the default vega datetime formatter, because it renders
        // datetimes in the local timezone. However, in the custom formatter we render datetimes in UTC.
        // To fix this, we assume that the timestamp received from server is offset by UTC-localTZ and
        // undo the offset before passing it to the formatter.
        const offsetMinutes = date.getTimezoneOffset();
        // we convert val to date iso string as that's the value that the
        // datetime formatter expects for datetimes
        const value = new Date(
          date.getTime() - offsetMinutes * 60 * 1000,
        ).toISOString();
        return formatter?.({ value }) ?? val;
      },
    );
  }
};
